/**
 * Haptic pattern allowlist + safe trigger.
 *
 * PATTERNS is the single greppable answer to "what can vibrate, and how hard?"
 * Every call site references a named HapticPattern from here, never an inline
 * navigator.vibrate() array, so intensity stays consistent and auditable.
 * Adding a pattern is deliberate: add the constant here, pair it with a visual
 * cue at the call site.
 *
 * Platform reality: navigator.vibrate() works on Android Chrome and never on
 * iOS Safari (no PWA wrapper exists), so haptics are strictly progressive
 * enhancement. haptic() is best-effort and never throws: it no-ops when the
 * user disabled haptics (settings toggle), when the device/browser lacks
 * support, or when the user prefers reduced motion, and it coalesces bursts
 * within a 40ms window so rapid-fire triggers feel like one tap, not a rattle.
 *
 * Pure module — no React. Component code should go through useHaptics()
 * (src/hooks/useHaptics.ts); the settings toggle syncs the enabled flag via
 * the UI store (src/stores/ui.store.ts).
 */

export type HapticPattern =
  | 'selection' | 'impactLight' | 'impactMedium' | 'impactHeavy'
  | 'success' | 'warning' | 'error' | 'rare';

/** Vibration payloads in ms (single pulse) or [pulse, pause, pulse, ...]. */
export const PATTERNS: Record<HapticPattern, number | number[]> = {
  selection: 10,
  impactLight: 15,
  impactMedium: 25,
  impactHeavy: 40,
  success: [15, 40, 25],
  warning: [25, 60, 25],
  error: [40, 30, 40, 30, 40],
  rare: [20, 40, 20, 40, 70],
};

/** Bursts within this window collapse into the first trigger. */
const COALESCE_WINDOW_MS = 40;

let enabled = true;
let lastFiredAt = -Infinity;

/** SSR-safe capability check: does this environment support vibration? */
export function canVibrate(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.vibrate === 'function'
  );
}

/**
 * Master enable/disable switch (default: enabled). The UI store's
 * setHapticsEnabled action calls this so the pure module stays in sync with
 * the persisted user preference.
 */
export function setHapticsEnabled(v: boolean): void {
  enabled = v;
}

function prefersReducedMotion(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch {
    return false;
  }
}

/**
 * Fire a named haptic pattern. Never throws; silently no-ops when disabled,
 * unsupported, reduced-motion is preferred, or within the coalesce window.
 */
export function haptic(p: HapticPattern): void {
  if (!enabled || !canVibrate() || prefersReducedMotion()) return;

  const now = performance.now();
  if (now - lastFiredAt < COALESCE_WINDOW_MS) return;
  lastFiredAt = now;

  try {
    navigator.vibrate(PATTERNS[p]);
  } catch {
    // Vibration is best-effort; never break the caller.
  }
}

/** Test-only: reset module state (enabled flag + coalesce window). */
export function resetHaptics(): void {
  enabled = true;
  lastFiredAt = -Infinity;
}
